/**
 * Vanguard dungeon enhancement eligibility, persistence helpers, combat mods.
 */

import {
  VANGUARD_ENHANCEMENTS,
  getEnhancementDef,
  type VanguardEnhancementDef,
} from '../data/vanguardEnhancements';
import { findDieWithSkill } from './skills';
import type { DiceTrayState, SkillOffer, TemporaryProgress } from './types';

export interface EnhancementCombatMods {
  primaryDamageBonusPct: number;
  cleaveSplashBonusPp: number;
  guaranteedBleedBonus: number;
  bloodlustHealBonusPp: number;
  twinStrikeDamageBonusPct: number;
  twinStrikeCritBonusPp: number;
}

export function emptyEnhancementCombatMods(): EnhancementCombatMods {
  return {
    primaryDamageBonusPct: 0,
    cleaveSplashBonusPp: 0,
    guaranteedBleedBonus: 0,
    bloodlustHealBonusPp: 0,
    twinStrikeDamageBonusPct: 0,
    twinStrikeCritBonusPp: 0,
  };
}

/** Aggregate combat modifiers from selected enhancement ids. */
export function combatModsFromEnhancements(
  selectedIds: readonly string[],
): EnhancementCombatMods {
  const mods = emptyEnhancementCombatMods();
  for (const id of selectedIds) {
    const def = getEnhancementDef(id);
    if (!def?.implemented) continue;
    switch (def.effect.type) {
      case 'primary_damage_bonus_pct':
        mods.primaryDamageBonusPct += def.effect.amount;
        break;
      case 'cleave_splash_bonus_pp':
        mods.cleaveSplashBonusPp += def.effect.amount;
        break;
      case 'guaranteed_bleed_bonus':
        mods.guaranteedBleedBonus += def.effect.amount;
        break;
      case 'bloodlust_heal_bonus_pp':
        mods.bloodlustHealBonusPp += def.effect.amount;
        break;
      case 'twin_strike_damage_bonus_pct':
        mods.twinStrikeDamageBonusPct += def.effect.amount;
        break;
      case 'twin_strike_crit_bonus_pp':
        mods.twinStrikeCritBonusPp += def.effect.amount;
        break;
      default:
        break;
    }
  }
  return mods;
}

export function selectedEnhancementIds(
  progress: TemporaryProgress,
): string[] {
  return [...(progress.selectedEnhancementIds ?? [])];
}

function pairTaken(
  progress: TemporaryProgress,
  pairId: string,
): boolean {
  return selectedEnhancementIds(progress).some((id) => {
    const def = getEnhancementDef(id);
    return def?.pairId === pairId;
  });
}

/**
 * Enhancements eligible for the current three-choice offer:
 * - parent skill owned on a die this run
 * - permanent unlock of parent (via unlocked actives) OR provisional pool ownership
 * - pair not already chosen
 * - implemented only (unimplemented flagged in data, never offered)
 */
export function eligibleEnhancementsForOffer(
  progress: TemporaryProgress,
  tray: DiceTrayState,
  unlockedActiveSkillIds: readonly string[],
): VanguardEnhancementDef[] {
  if (progress.awaitingFirstPrimary) {
    return [];
  }
  const ownedParents = new Set<string>();
  for (const die of tray.dice) {
    if (die.unlocked && die.primarySkillId) {
      ownedParents.add(die.primarySkillId);
    }
  }
  // Parent must be acquired this run (on a die). Permanent unlock alone is not enough.
  const result: VanguardEnhancementDef[] = [];
  for (const def of VANGUARD_ENHANCEMENTS) {
    if (!def.implemented) continue;
    if (!ownedParents.has(def.parentSkillId)) continue;
    // If player has permanent unlocks, only those parents; if provisional pool, any owned.
    if (
      unlockedActiveSkillIds.length > 0 &&
      !unlockedActiveSkillIds.includes(def.parentSkillId)
    ) {
      continue;
    }
    if (pairTaken(progress, def.pairId)) continue;
    result.push(def);
  }
  return result;
}

export function buildEnhancementOffer(
  def: VanguardEnhancementDef,
): SkillOffer {
  return {
    offerId: def.id,
    kind: 'enhancement',
    skillId: def.parentSkillId,
    enhancementId: def.id,
    isUpgrade: false,
    currentLevel: 0,
    nextLevel: 1,
    exclusionNote: `Conflicts with ${def.conflictsWithName}`,
    displayName: def.name,
    displayDescription: def.description,
  };
}

/**
 * Accept an enhancement card — no die assignment. Records selection and
 * excludes the whole pair for the rest of the run.
 */
export function acceptEnhancementOffer(
  progress: TemporaryProgress,
  enhancementId: string,
): TemporaryProgress {
  if (!progress.currentOffer) {
    throw new Error('No active skill offer');
  }
  if (progress.pendingLevelUps <= 0) {
    throw new Error('No pending level-ups');
  }
  const offer = progress.currentOffer.find((o) => o.offerId === enhancementId);
  if (!offer || offer.kind !== 'enhancement' || !offer.enhancementId) {
    throw new Error(`Enhancement ${enhancementId} is not in the current offer`);
  }
  const def = getEnhancementDef(offer.enhancementId);
  if (!def?.implemented) {
    throw new Error(`Enhancement ${enhancementId} is not implemented`);
  }
  if (pairTaken(progress, def.pairId)) {
    throw new Error(`Enhancement pair ${def.pairId} already chosen`);
  }
  // Parent must still be on a die (caller validates tray separately if needed).
  const selected = selectedEnhancementIds(progress);
  return {
    ...progress,
    selectedEnhancementIds: [...selected, def.id],
    pendingLevelUps: progress.pendingLevelUps - 1,
    awaitingFirstPrimary: false,
    currentOffer: null,
    offerKind: null,
  };
}

/** True when the tray still owns the parent skill for this enhancement. */
export function enhancementParentOwned(
  tray: DiceTrayState,
  enhancementId: string,
): boolean {
  const def = getEnhancementDef(enhancementId);
  if (!def) return false;
  return findDieWithSkill(tray, def.parentSkillId) != null;
}
